// The Apps tab: the applications this process's work is done in, how the active time splits
// between them, and how widely each is used. Picking an app lights the steps done in it, so the
// table and the map stay two readings of the same process.
import { ChevronDown } from "lucide-react";
import React from "react";
import { useEffect, useId, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { formatDuration } from "../../utils";
import { INSIGNIFICANT_COLOR, getNodeColor } from "../analytics/palette";
import { appUsageTable } from "./processAppUsage";
import type { ProcessGraphNode, ProcessInstance } from "./processGraphModel";
import { HelpHint, KpiTiles, PanelSection, StepAppIcon } from "./processSidebarUi";

const GRID =
  "grid grid-cols-[minmax(0,1fr)_4rem_3rem_4rem] items-center gap-x-3 @lg:grid-cols-[minmax(0,1fr)_4.5rem_3.5rem_4.5rem_3.5rem] @lg:gap-x-4";

const ProcessAppsTab = ({
  instances,
  steps,
  onFocusSteps,
}: {
  instances: ProcessInstance[];
  steps: ProcessGraphNode[];
  /** Lights the given steps on the map; null lets go. */
  onFocusSteps: (stepIds: string[] | null) => void;
}) => {
  const { t } = useTranslation();
  const { rows, other, totalSeconds } = useMemo(
    () => appUsageTable(instances, steps),
    [instances, steps],
  );
  const [pinned, setPinned] = useState<string | null>(null);
  const [hovered, setHovered] = useState<string | null>(null);
  const [otherExpanded, setOtherExpanded] = useState(false);
  const otherId = useId();
  const focusedApp = hovered ?? pinned;
  const focusedSteps = rows.find(row => row.app === focusedApp)?.stepIds ?? null;
  const focusKey = focusedSteps?.join("\u0000") ?? null;

  // The map follows the pointed-at (or picked) app and lets go when the tab goes away.
  // biome-ignore lint/correctness/useExhaustiveDependencies: focusKey stands for focusedSteps.
  useEffect(() => {
    if (!focusedSteps) return;
    onFocusSteps(focusedSteps);
    return () => onFocusSteps(null);
  }, [focusKey, onFocusSteps]);

  const caseCount = instances.length;
  const main = rows[0];
  const color = (app: string) => getNodeColor(app);

  if (rows.length === 0) {
    return <p className="text-sm text-text-dim">{t("processPanel.noApps")}</p>;
  }

  return (
    <div className="@container">
      <KpiTiles
        items={[
          { label: t("processPanel.metricAppsUsed"), value: rows.length },
          { label: t("processPanel.metricTimeInApps"), value: formatDuration(totalSeconds) },
          {
            label: t("processPanel.metricTimePerCase"),
            value: formatDuration(caseCount > 0 ? totalSeconds / caseCount : 0),
          },
          {
            label: t("processPanel.metricMainApp"),
            value: (
              <span className="flex min-w-0 items-center gap-1.5" title={main.app}>
                <StepAppIcon app={main.app} className="h-5 w-5" fallback="none" />
                <span className="truncate">{main.app}</span>
              </span>
            ),
          },
        ]}
      />

      <PanelSection title={t("processPanel.timeByApp")}>
        {/* Pointing at a segment lights its row below, which already reads its time and share. */}
        <div
          role="img"
          aria-label={rows.map(row => `${row.app} ${Math.round(row.share * 100)}%`).join(", ")}
          className="flex h-3.5 w-full gap-px overflow-hidden rounded-full bg-app-usage-track"
          onPointerLeave={() => setHovered(null)}
        >
          {rows.map(row => (
            <span
              key={row.app}
              onPointerEnter={() => setHovered(row.app)}
              className={`h-full transition-opacity ${
                focusedApp && focusedApp !== row.app ? "opacity-30" : ""
              }`}
              style={{ width: `${row.share * 100}%`, background: color(row.app) }}
            />
          ))}
          {other.seconds > 0 && (
            <span
              onPointerEnter={() => setHovered(null)}
              className="h-full"
              style={{
                width: `${(other.seconds / totalSeconds) * 100}%`,
                background: INSIGNIFICANT_COLOR,
              }}
            />
          )}
        </div>

        <div className="mt-3 overflow-hidden rounded-lg border border-outline">
          <div
            className={`${GRID} border-b border-outline bg-surface-strong/60 px-3 py-2 text-[11px] font-medium uppercase tracking-wide text-text-dim`}
          >
            <span>{t("processPanel.columnApp")}</span>
            <span className="text-right">{t("processPanel.columnTime")}</span>
            <span className="text-right">{t("processPanel.columnShare")}</span>
            <span className="text-right">{t("processPanel.columnCasesUsing")}</span>
            <span className="hidden items-center justify-end gap-0.5 @lg:flex">
              {t("processPanel.columnSteps")}
              <HelpHint
                standalone
                label={t("processPanel.columnSteps")}
                text={t("processPanel.columnStepsHelp")}
              />
            </span>
          </div>
          <ul className="divide-y divide-outline" onPointerLeave={() => setHovered(null)}>
            {rows.map(row => (
              <li key={row.app}>
                <button
                  type="button"
                  aria-pressed={pinned === row.app}
                  aria-label={t("processPanel.highlightApp", { app: row.app })}
                  title={t("processPanel.highlightApp", { app: row.app })}
                  disabled={row.stepIds.length === 0}
                  onClick={() => setPinned(current => (current === row.app ? null : row.app))}
                  onPointerEnter={() => setHovered(row.app)}
                  className={`${GRID} w-full px-3 py-2.5 text-left text-sm transition hover:bg-surface-strong disabled:cursor-default focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/30 ${
                    hovered === row.app
                      ? "bg-surface-strong"
                      : pinned === row.app
                        ? "bg-focus-band"
                        : ""
                  }`}
                >
                  <span className="flex min-w-0 items-center gap-2">
                    <span
                      aria-hidden="true"
                      className="h-2 w-2 shrink-0 rounded-sm"
                      style={{ background: color(row.app) }}
                    />
                    <StepAppIcon app={row.app} fallback="space" />
                    <span className="min-w-0 truncate font-medium text-text">{row.app}</span>
                  </span>
                  <span className="text-right tabular-nums text-text-mid">
                    {formatDuration(row.seconds)}
                  </span>
                  <span className="text-right tabular-nums text-text-mid">
                    {Math.round(row.share * 100)}%
                  </span>
                  <span className="text-right tabular-nums text-text-mid">
                    {row.cases}/{caseCount}
                  </span>
                  <span className="hidden text-right tabular-nums text-text-mid @lg:block">
                    {row.stepIds.length}
                  </span>
                </button>
              </li>
            ))}
            {other.rows.length > 0 && (
              <li>
                <button
                  type="button"
                  aria-expanded={otherExpanded}
                  aria-controls={otherId}
                  onClick={() => setOtherExpanded(current => !current)}
                  onPointerEnter={() => setHovered(null)}
                  className={`${GRID} w-full px-3 py-2.5 text-left text-sm text-text-dim transition hover:bg-surface-strong focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/30`}
                >
                  <span className="flex min-w-0 items-center gap-2">
                    <span
                      aria-hidden="true"
                      className="h-2 w-2 shrink-0 rounded-sm"
                      style={{ background: INSIGNIFICANT_COLOR }}
                    />
                    <ChevronDown
                      aria-hidden="true"
                      className={`h-4 w-4 shrink-0 transition-transform ${otherExpanded ? "rotate-180" : ""}`}
                    />
                    <span className="min-w-0 truncate font-medium">{t("other_apps")}</span>
                  </span>
                  <span className="text-right tabular-nums">{formatDuration(other.seconds)}</span>
                  <span className="text-right tabular-nums">
                    {Math.round((other.seconds / totalSeconds) * 100)}%
                  </span>
                  <span className="text-right">—</span>
                  <span className="hidden text-right @lg:block">—</span>
                </button>
                <div id={otherId} hidden={!otherExpanded} className="bg-surface-strong/30">
                  <p className="px-3 py-2 text-xs text-text-dim">
                    {t("processPanel.otherAppsHelp")}
                  </p>
                  <ul className="divide-y divide-outline">
                    {other.rows.map(row => (
                      <li key={row.app} className={`${GRID} px-3 py-2.5 text-sm text-text-mid`}>
                        <span className="flex min-w-0 items-center gap-2 pl-4">
                          <StepAppIcon app={row.app} fallback="space" />
                          <span className="min-w-0 truncate" title={row.app}>
                            {row.app}
                          </span>
                        </span>
                        <span className="text-right tabular-nums">
                          {formatDuration(row.seconds)}
                        </span>
                        <span className="text-right tabular-nums">
                          {row.share * 100 < 0.1 ? "<0.1" : (row.share * 100).toFixed(1)}%
                        </span>
                        <span className="text-right tabular-nums">
                          {row.cases}/{caseCount}
                        </span>
                        <span className="hidden text-right tabular-nums @lg:block">
                          {row.stepIds.length}
                        </span>
                      </li>
                    ))}
                  </ul>
                </div>
              </li>
            )}
          </ul>
        </div>
      </PanelSection>
    </div>
  );
};

export default ProcessAppsTab;
